var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { Text, Icon, Spacing, Inset } from '@doordash/design-language';
import Banner from '@doordash/component-banner';
import MembersStrings from './MembersStrings';
import { useSimpleStore } from '../../utils/simpleStore';
import { DV2_NAMES, dv2Defaults } from '../DV2';
import { useDVSBoolean } from '@doordash/lib-dvs-client/lib/react';
import ResendInvitesConfirmationModal from './ResendInvitesConfirmationModal';
import { useResendInvitesToAllPendingMembersMutation } from './useResendInvitesToAllPendingMembers';
import { getTeamId, getTeamName } from '../../selectors/portal_selector';
import { getSelectedTeamAccount } from '../../selectors/uiSelector';
import { useToast } from '@doordash/component-toast';
import { captureSentryEvent } from '../../utils/errorHandling';
import { ProductFamily, ProductWorkflow, } from '../../types/_ProductSpecificTypes';
import track from '../../utils/track';
import { MembershipActivationEvents } from '../../constants/events/names';
var CLOSE_RESEND_INVITE_BANNER_COUNT = 'ddweb-resend-invite-close-count';
var CLOSE_BANNER_MAX_COUNT = 3;
export var ResendWorkPortalInviteBanner = function (_a) {
    var totalPending = _a.totalPending, totalCount = _a.totalCount;
    var isMembershipActivationRemindersEnabled = useDVSBoolean(DV2_NAMES.ddfwMembershipActivationReminder, dv2Defaults.ddfwMembershipActivationReminder);
    var simpleStore = useSimpleStore();
    var _b = __read(React.useState(true), 2), showBanner = _b[0], setShowBanner = _b[1];
    var _c = __read(React.useState(false), 2), showConfirmation = _c[0], setShowConfirmation = _c[1];
    var displayToast = useToast().displayToast;
    var teamId = useSelector(getTeamId) || '';
    var teamName = useSelector(getTeamName) || '';
    var selectedTeamAccount = useSelector(getSelectedTeamAccount);
    var _d = __read(React.useState(false), 2), shouldDisplayError = _d[0], setShouldDisplayError = _d[1];
    var _e = useResendInvitesToAllPendingMembersMutation(), resendInvites = _e.resendInvites, isSending = _e.isSending;
    var strings = MembersStrings.resendWorkPortalInviteBanner;
    var isPlural = totalPending > 1;
    var closeBannerCount = simpleStore.get(CLOSE_RESEND_INVITE_BANNER_COUNT) || 0;
    var shouldDisplayBanner = isMembershipActivationRemindersEnabled &&
        showBanner &&
        totalPending !== 0 &&
        closeBannerCount <= CLOSE_BANNER_MAX_COUNT;
    var handleCloseBanner = function () {
        setShowBanner(false);
        track(MembershipActivationEvents.CLOSE_RESEND_INVITES_BANNER, {
            team_account_id: selectedTeamAccount,
            close_count: closeBannerCount,
            pending_member_count: totalPending,
            total_member_count: totalCount,
        });
        simpleStore.set(CLOSE_RESEND_INVITE_BANNER_COUNT, closeBannerCount + 1);
    };
    var handleCloseConfirmationModal = function () {
        setShowConfirmation(false);
        setShouldDisplayError(false);
    };
    var captureErrorEvents = function (severity, message, errData) {
        captureSentryEvent(severity, message, {
            productFamily: ProductFamily.ExpensedMeals,
            productWorkflow: ProductWorkflow.SendJoinInvitationToAllPendingMembers,
        }, {
            teamId: teamId,
            teamName: teamName,
            selectedTeamAccount: selectedTeamAccount,
            error: errData,
        });
        track(MembershipActivationEvents.RESND_INVITES_FAILED, {
            team_account_id: selectedTeamAccount,
            bulk_send: true,
        });
    };
    var handleResendInvites = function () {
        resendInvites(teamId, teamName, selectedTeamAccount, {
            onCompleted: function (data) {
                var _a, _b;
                if ((_a = data.sendJoinInvitationEmailToAllPendingTeamMembers) === null || _a === void 0 ? void 0 : _a.success) {
                    handleCloseConfirmationModal();
                    displayToast({
                        icon: Icon.Types.Check,
                        text: MembersStrings.resendInvitesSuccessToast(totalPending, isPlural),
                        insetFromEdge: Inset.Sizes.XxxLarge,
                    });
                    track(MembershipActivationEvents.RESEND_INVITES_SUCCESS, {
                        team_account_id: selectedTeamAccount,
                        bulk_send: true,
                        pending_member_count: totalPending,
                        total_member_count: totalCount,
                    });
                }
                else {
                    setShouldDisplayError(true);
                    captureErrorEvents(Sentry.Severity.Log, 'BE errors for sending join invitation to all pending members', (_b = data.sendJoinInvitationEmailToAllPendingTeamMembers) === null || _b === void 0 ? void 0 : _b.error);
                }
            },
            onError: function (error) {
                setShouldDisplayError(true);
                captureErrorEvents(Sentry.Severity.Error, 'encountered errors when sending join invitation to all pending members', error);
            },
        });
    };
    return (React.createElement(React.Fragment, null,
        shouldDisplayBanner && (React.createElement(Banner, { iconType: Icon.Types.InsightLine, body: React.createElement(Text, { styles: Text.Styles.BannerBody, lineHeight: Spacing.XLarge }, isPlural
                ? strings.multiEmployeesText(totalPending)
                : strings.singleEmployeeText(totalPending)), primaryAction: {
                content: strings.primaryActionContent(),
                onClick: function () {
                    setShowConfirmation(true);
                },
            }, onClose: handleCloseBanner })),
        showConfirmation && (React.createElement(ResendInvitesConfirmationModal, { totalPending: totalPending, isSending: isSending, handleResendInvites: handleResendInvites, shouldDisplayError: shouldDisplayError, onClose: handleCloseConfirmationModal }))));
};
